import { jsPDF } from 'jspdf';
import { drawPdfHeader, drawSectionTitle } from '../../Wartung/shared/pdf-branding.util';
import { TakedaAbrechnung, TakedaAbrechnungBlock } from '../../../Models/stammkunde.model';

/*
 * Regie-Abrechnung als ein Projekt-PDF: zuerst Übersicht mit Summen, danach ein
 * Detailabschnitt je Regieauftrag (RA001, RA002, …).
 *
 * Nutzt dieselbe Briefkopf-/Abschnittsgestaltung wie der bestehende
 * Einsatz-/Reparaturbericht (pdf-branding.util), damit alle Dokumente des
 * Systems gleich aussehen.
 *
 * WICHTIG: Hier wird NICHTS gerechnet. Sämtliche Beträge und Summen kommen
 * fertig aus dem TakedaAbrechnungService; das PDF setzt sie nur. Dadurch zeigen
 * Projektansicht, PDF und Excel garantiert dieselben Werte – und es gibt keine
 * Floating-Point-Abweichungen aus JavaScript.
 */

const RAND = 12;
const ZEILE = 5;

interface Spalte {
  titel: string;
  breite: number;
  rechts?: boolean;
}

function seitenBreite(doc: jsPDF): number {
  return doc.internal.pageSize.getWidth();
}

function seitenHoehe(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight();
}

/** Sorgt für einen sauberen Seitenumbruch, bevor etwas abgeschnitten wird. */
function platz(doc: jsPDF, y: number, benoetigt: number): number {
  if (y + benoetigt <= seitenHoehe(doc) - 16) {
    return y;
  }
  doc.addPage();
  return 20;
}

function geld(wert: number | null | undefined): string {
  if (wert == null) { return '–'; }
  return `${wert.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} €`;
}

function zahl(wert: number | null | undefined, maxNachkomma = 2): string {
  if (wert == null) { return '–'; }
  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: maxNachkomma
  });
}

function datum(iso: string | null | undefined): string {
  if (!iso) { return '–'; }
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '–' : d.toLocaleDateString('de-DE');
}

/** Kürzt Text, der nicht in seine Spalte passt – verhindert Überlappungen. */
function kuerzen(doc: jsPDF, text: string, breite: number): string {
  if (doc.getTextWidth(text) <= breite) { return text; }

  let gekuerzt = text;
  while (gekuerzt.length > 1 && doc.getTextWidth(`${gekuerzt}…`) > breite) {
    gekuerzt = gekuerzt.slice(0, -1);
  }
  return `${gekuerzt}…`;
}

function tabellenKopf(doc: jsPDF, y: number, spalten: Spalte[]): number {
  doc.setFillColor(238, 242, 248);
  doc.rect(RAND, y - 4, seitenBreite(doc) - 2 * RAND, 6.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 80);

  let x = RAND + 2;
  for (const s of spalten) {
    doc.text(s.titel, s.rechts ? x + s.breite - 2 : x, y, { align: s.rechts ? 'right' : 'left' });
    x += s.breite;
  }

  return y + 6;
}

function tabellenZeile(doc: jsPDF, y: number, spalten: Spalte[], werte: string[]): number {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(30, 30, 40);

  let x = RAND + 2;
  for (let i = 0; i < spalten.length; i++) {
    const s = spalten[i];
    const text = kuerzen(doc, werte[i] ?? '', s.breite - 4);
    doc.text(text, s.rechts ? x + s.breite - 2 : x, y, { align: s.rechts ? 'right' : 'left' });
    x += s.breite;
  }

  doc.setDrawColor(228, 232, 240);
  doc.line(RAND, y + 1.5, seitenBreite(doc) - RAND, y + 1.5);

  return y + ZEILE;
}

function summenZeile(doc: jsPDF, y: number, label: string, wert: string, fett = false): number {
  const breite = seitenBreite(doc) - 2 * RAND;

  if (fett) {
    doc.setFillColor(238, 242, 248);
    doc.rect(RAND, y - 4, breite, 6.5, 'F');
  }

  doc.setFont('helvetica', fett ? 'bold' : 'normal');
  doc.setFontSize(fett ? 10 : 9);
  doc.setTextColor(26, 26, 46);

  doc.text(label, RAND + 2, y);
  doc.text(wert, seitenBreite(doc) - RAND - 2, y, { align: 'right' });

  return y + (fett ? 8 : 6);
}

function infoZeile(doc: jsPDF, y: number, label: string, wert: string): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(90, 95, 110);
  doc.text(label, RAND, y);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(26, 26, 46);
  doc.text(wert, RAND + 34, y);

  return y + 5.5;
}

/** Baut das vollständige Abrechnungs-PDF und liefert das jsPDF-Dokument. */
export async function buildTakedaAbrechnungPdf(a: TakedaAbrechnung): Promise<jsPDF> {
  const doc = new jsPDF();

  let y = await drawPdfHeader(
    doc,
    'Takeda – Regieübersicht',
    `${a.titel}${a.orderNumber ? ` · ${a.referenzLabel} ${a.orderNumber}` : ''}`
  );

  y += 4;

  // ── Projektkopf ──
  y = infoZeile(doc, y, 'Kunde', a.customerName || '–');
  y = infoZeile(doc, y, 'Projekt', a.titel);
  y = infoZeile(doc, y, a.referenzLabel, a.orderNumber ?? '–');
  y = infoZeile(doc, y, 'Bau / Ort', a.ort || '–');
  y = infoZeile(doc, y, 'Status', a.statusLabel);

  if (a.von && a.bis) {
    y = infoZeile(doc, y, 'Zeitraum', `${datum(a.von)} – ${datum(a.bis)}`);
  }

  y += 4;

  // ── Summen ──
  y = drawSectionTitle(doc, y, 'Summen');
  y = summenZeile(doc, y, 'Stunden gesamt', geld(a.stundenSumme));
  y = summenZeile(doc, y, 'Material gesamt', geld(a.materialSumme));
  y = summenZeile(doc, y, 'Gesamt', geld(a.gesamtSumme), true);

  y += 4;

  // ── Übersichtstabelle ──
  y = platz(doc, y, 30);
  y = drawSectionTitle(doc, y, 'Regieaufträge');

  const uebersicht: Spalte[] = [
    { titel: 'Nr.', breite: 16 },
    { titel: 'Datum', breite: 20 },
    { titel: 'Bau / Ort', breite: 30 },
    { titel: 'Arbeitsbereich', breite: 56 },
    { titel: 'Stunden', breite: 22, rechts: true },
    { titel: 'Material', breite: 22, rechts: true },
    { titel: 'Gesamt', breite: 20, rechts: true }
  ];

  y = tabellenKopf(doc, y, uebersicht);

  for (const block of a.bloecke) {
    y = platz(doc, y, 10);
    y = tabellenZeile(doc, y, uebersicht, [
      block.nummer,
      datum(block.workDate),
      block.ort || '–',
      block.beschreibung || '–',
      geld(block.stundenSumme),
      geld(block.materialSumme),
      geld(block.summe)
    ]);
  }

  y += 2;
  y = summenZeile(doc, y, 'Gesamtsumme', geld(a.gesamtSumme), true);

  // ── Detailabschnitte ──
  for (const block of a.bloecke) {
    doc.addPage();
    y = 20;
    y = zeichneBlock(doc, a, block, y);
  }

  fussZeilen(doc, a);

  return doc;
}

function zeichneBlock(
  doc: jsPDF,
  a: TakedaAbrechnung,
  block: TakedaAbrechnungBlock,
  startY: number
): number {
  let y = drawSectionTitle(doc, startY, `${block.nummer} · ${datum(block.workDate)}`);

  y = infoZeile(doc, y, 'Projekt', a.titel);
  y = infoZeile(doc, y, a.referenzLabel, block.abweichendeReferenz ?? a.orderNumber ?? '–');
  y = infoZeile(doc, y, 'Bau / Ort', block.ort || '–');

  if (block.assignmentTitle) {
    y = infoZeile(doc, y, 'Einsatz', block.assignmentTitle);
  }

  y += 2;

  // Beschreibung mit echtem Umbruch statt Kürzung.
  if (block.beschreibung) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(90, 95, 110);
    doc.text('Tätigkeit', RAND, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 40);
    const zeilen = doc.splitTextToSize(block.beschreibung, seitenBreite(doc) - 2 * RAND);

    for (const zeile of zeilen) {
      y = platz(doc, y, 8);
      doc.text(zeile, RAND, y);
      y += 4.5;
    }

    y += 3;
  }

  // ── Stunden ──
  if (block.stunden.length > 0) {
    y = platz(doc, y, 24);
    y = drawSectionTitle(doc, y, 'Stunden');

    const spalten: Spalte[] = [
      { titel: 'Bezeichnung', breite: 36 },
      { titel: 'Name', breite: 56 },
      { titel: 'Stundensatz', breite: 30, rechts: true },
      { titel: 'Stunden', breite: 26, rechts: true },
      { titel: 'Summe', breite: 38, rechts: true }
    ];

    y = tabellenKopf(doc, y, spalten);

    for (const s of block.stunden) {
      y = platz(doc, y, 10);
      y = tabellenZeile(doc, y, spalten, [
        s.rateLabel || '–',
        s.personName,
        geld(s.hourlyRate),
        zahl(s.hours),
        geld(s.betrag)
      ]);
    }

    y += 1;
    y = summenZeile(doc, y, 'Summe Stunden', geld(block.stundenSumme));
    y += 2;
  }

  // ── Material ──
  if (block.material.length > 0) {
    y = platz(doc, y, 24);
    y = drawSectionTitle(doc, y, 'Material');

    const spalten: Spalte[] = [
      { titel: 'LV-Pos.', breite: 26 },
      { titel: 'Bezeichnung', breite: 62 },
      { titel: 'Menge', breite: 20, rechts: true },
      { titel: 'Einheit', breite: 18 },
      { titel: 'Preis', breite: 28, rechts: true },
      { titel: 'Gesamt', breite: 32, rechts: true }
    ];

    y = tabellenKopf(doc, y, spalten);

    for (const m of block.material) {
      y = platz(doc, y, 10);
      y = tabellenZeile(doc, y, spalten, [
        // Nur echte LV-Positionen tragen eine Nummer; freies und übernommenes
        // Altmaterial erscheint als "frei".
        m.art === 'Lv' ? (m.lvNummer || '–') : 'frei',
        m.bezeichnung,
        zahl(m.menge, 4),
        m.einheit || '',
        geld(m.preis),
        geld(m.betrag)
      ]);
    }

    y += 1;
    y = summenZeile(doc, y, 'Summe Material', geld(block.materialSumme));
    y += 2;
  }

  y = platz(doc, y, 12);
  y = summenZeile(doc, y, `Summe ${block.nummer}`, geld(block.summe), true);

  return y;
}

function fussZeilen(doc: jsPDF, a: TakedaAbrechnung): void {
  const seiten = doc.getNumberOfPages();

  for (let i = 1; i <= seiten; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(140, 145, 160);

    const links = `${a.titel}${a.orderNumber ? ` · ${a.orderNumber}` : ''}`;
    doc.text(kuerzen(doc, links, 120), RAND, seitenHoehe(doc) - 8);
    doc.text(`Seite ${i} von ${seiten}`, seitenBreite(doc) - RAND, seitenHoehe(doc) - 8, { align: 'right' });
  }
}

/** Dateiname ohne für Dateisysteme ungültige Zeichen. */
export function takedaExportDateiname(a: TakedaAbrechnung, endung: string): string {
  const saeubere = (wert: string) =>
    wert.trim()
      .replace(/[^\p{L}\p{N}\-_]+/gu, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  const teile = ['Takeda', a.orderNumber ?? '', a.titel]
    .filter(t => !!t)
    .map(saeubere)
    .filter(t => t.length > 0);

  return `${teile.join('_').slice(0, 120)}.${endung}`;
}
